import React, { useState, useEffect, useRef } from 'react';

/**
 * HelpTooltip - Positioned tooltip that appears near an anchor element
 * @param {string} content - Tooltip text content
 * @param {string} position - Position relative to anchor ('top', 'bottom', 'left', 'right')
 * @param {HTMLElement} anchorElement - Element to position tooltip relative to
 * @param {function} onClose - Callback when tooltip should close
 */
export default function HelpTooltip({ content, position = 'top', anchorElement, onClose }) {
  const [tooltipStyle, setTooltipStyle] = useState({ visibility: 'hidden' });
  const tooltipRef = useRef(null);
  const retryCountRef = useRef(0);
  const maxRetries = 10;

  useEffect(() => {
    if (!anchorElement || !tooltipRef.current) return;

    retryCountRef.current = 0;

    const updatePosition = () => {
      if (!tooltipRef.current || !anchorElement) return;
      
      const anchorRect = anchorElement.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      // Wait for tooltip to have dimensions before positioning
      if (tooltipRect.width === 0 || tooltipRect.height === 0) {
        retryCountRef.current++;
        if (retryCountRef.current < maxRetries) {
          // Use requestAnimationFrame to wait for next render
          requestAnimationFrame(updatePosition);
        }
        return;
      }
      
      // Reset retry count on successful positioning
      retryCountRef.current = 0;
      
      const gap = 8; // Gap between anchor and tooltip

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = anchorRect.top - tooltipRect.height - gap;
          left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          top = anchorRect.bottom + gap;
          left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'left':
          top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
          left = anchorRect.left - tooltipRect.width - gap;
          break;
        case 'right':
          top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
          left = anchorRect.right + gap;
          break;
        default:
          top = anchorRect.top - tooltipRect.height - gap;
          left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
      }

      // Ensure tooltip stays within viewport
      const padding = 12;
      if (top < padding) {
        top = padding;
      }
      if (left < padding) {
        left = padding;
      }
      if (top + tooltipRect.height > window.innerHeight - padding) {
        top = window.innerHeight - tooltipRect.height - padding;
      }
      if (left + tooltipRect.width > window.innerWidth - padding) {
        left = window.innerWidth - tooltipRect.width - padding;
      }

      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 10000,
        visibility: 'visible',
      });
    };

    // Initial positioning with delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(updatePosition);
    }, 10);

    // Update on scroll/resize (use passive listeners for better performance)
    let rafId = null;
    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updatePosition);
    };
    const handleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updatePosition);
    };

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleResize);
    };
  }, [anchorElement, position]);

  if (!content || !anchorElement) return null;

  // Determine arrow position
  const arrowStyle = {
    position: 'absolute',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  };

  switch (position) {
    case 'top':
      arrowStyle.bottom = '-6px';
      arrowStyle.left = '50%';
      arrowStyle.transform = 'translateX(-50%)';
      arrowStyle.borderWidth = '6px 6px 0 6px';
      arrowStyle.borderColor = '#374151 transparent transparent transparent';
      break;
    case 'bottom':
      arrowStyle.top = '-6px';
      arrowStyle.left = '50%';
      arrowStyle.transform = 'translateX(-50%)';
      arrowStyle.borderWidth = '0 6px 6px 6px';
      arrowStyle.borderColor = 'transparent transparent #374151 transparent';
      break;
    case 'left':
      arrowStyle.right = '-6px';
      arrowStyle.top = '50%';
      arrowStyle.transform = 'translateY(-50%)';
      arrowStyle.borderWidth = '6px 0 6px 6px';
      arrowStyle.borderColor = 'transparent transparent transparent #374151';
      break;
    case 'right':
      arrowStyle.left = '-6px';
      arrowStyle.top = '50%';
      arrowStyle.transform = 'translateY(-50%)';
      arrowStyle.borderWidth = '6px 6px 6px 0';
      arrowStyle.borderColor = 'transparent #374151 transparent transparent';
      break;
  }

  return (
    <div
      ref={tooltipRef}
      style={{
        ...tooltipStyle,
        background: '#374151',
        color: '#fff',
        padding: '8px 12px',
        borderRadius: 6,
        fontSize: 13,
        lineHeight: 1.5,
        maxWidth: 300,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        pointerEvents: 'none',
      }}
      role="tooltip"
    >
      {content}
      <div style={arrowStyle} />
    </div>
  );
}

